<template>
  <div
      v-if="isOpen"
      :id="id"
      class="mfa-overlay"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="`${id}-title`"
      :aria-describedby="`${id}-description`"
  >
    <div class="mfa-backdrop" />

    <div class="mfa-dialog">
      <!-- Header -->
      <div class="mfa-header">
        <h2 :id="`${id}-title`" class="mfa-title">Enter Verification Code</h2>
        <button type="button" class="mfa-close" aria-label="Close dialog" @click="close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <!-- Body -->
      <div class="mfa-body">
        <p :id="`${id}-description`" class="mfa-description">{{ description }}</p>

        <form class="mfa-form" novalidate @submit.prevent="submit">
          <div class="mfa-field">
            <label class="mfa-label" :for="`${id}-input`">Verification code</label>
            <input
                v-model="code"
                ref="codeInput"
                class="mfa-input"
                :class="{ 'is-filled': code, 'is-error': hasError }"
                type="text"
                inputmode="text"
                pattern="[A-Z0-9]"
                :maxlength="codeLength"
                autocomplete="off"
                :aria-label="`Code input`"
                :id="`${id}-input`"
                @input="onInput()"
                @paste.prevent="onPaste($event)"
            />
            <p
                v-if="hasError"
                :id="`${id}-error`"
                class="mfa-error"
                role="alert"
                aria-live="polite"
            >
              {{ errorMessage }}
            </p>
          </div>

          <p v-if="expiryText" class="mfa-description">{{ expiryText }}</p>
        </form>
      </div>

      <!-- Footer -->
      <div class="mfa-footer">
        <button
            v-if="showResend"
            type="button"
            class="mfa-btn mfa-btn--resend"
            :class="{ 'is-loading': isResending }"
            :disabled="isResending || resendBlocked"
            @click="resend"
        >
          {{ resendBlocked ? `Resend Code (${resendCountdown}s)` : 'Resend Code' }}
        </button>
        <button
            type="button"
            class="mfa-btn mfa-btn--primary"
            :class="{ 'is-loading': isLoading }"
            :disabled="isLoading || !isComplete"
            @click="submit"
        >
          Verify
        </button>
      </div>
    </div>
  </div>
</template>
<script>

import {MFA_METHODS} from "./mfaMethods";

const generic_descriptions = {
  [MFA_METHODS.TOTP]:  'generated by your Authenticator App',
  [MFA_METHODS.EMAIL]: 'sent to your email',
  [MFA_METHODS.SMS]: 'sent to your phone',
};

export default {
  name: 'MfaModal',

  props: {
    id: {
      type: String,
      default: 'mfa-modal',
    },
    mfaMethod: {
      type: String,
      default: MFA_METHODS.TOTP,
      validator: (value) => Object.values(MFA_METHODS).includes(value),
    },
    destination: {
      type: String,
      default: null,
    },
    codeLength: {
      type: Number,
      default: 6,
    },
    // Control visibility from the parent via v-model
    value: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      code: '',
      errorMessage: '',
      isLoading: false,
      isResending: false,
      resendCountdown: 0,
    };
  },

  computed: {
    isOpen() {
      return this.value;
    },
    hasError() {
      return this.errorMessage.length > 0;
    },
    isComplete() {
      return this.code.length === this.codeLength;
    },
    description() {
      if (this.mfaMethod === MFA_METHODS.TOTP) {
        return 'Enter the verification code generated by your Authenticator App'
      }

      if (this.destination !== null && this.destination !== undefined && this.destination.length > 0) {
        let prefix = this.mfaMethod === MFA_METHODS.EMAIL
            ? 'An email'
            : this.mfaMethod === MFA_METHODS.SMS ? 'A text message' : 'A message'
        return `${prefix} with your code has been sent to ${this.destination}`
      }

      const suffix = generic_descriptions[this.mfaMethod];
      if (suffix == null) {
        return `Enter your verification code`
      }
      return `Enter the verification code ${suffix}`;
    },
    expiryText() {
      return this.mfaMethod !== MFA_METHODS.TOTP
          ? 'This code expires in 10 minutes'
          : '';
    },
    showResend() {
      return this.mfaMethod !== MFA_METHODS.TOTP;
    },
    resendBlocked() {
      return this.resendCountdown > 0;
    },
  },

  watch: {
    // Focus input when the modal opens
    isOpen(val) {
      if (val) {
        if (this.showResend) {
          this.startResendCountdown();
        }

        this.$nextTick(() => {
          this.$refs.codeInput.focus();

          document.body.style.overflow = 'hidden';
        });
      } else {
        document.body.style.overflow = '';
      }
    },
  },

  mounted() {
    document.addEventListener('keydown', this.onEscape);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.onEscape);
    document.body.style.overflow = '';
  },

  methods: {
    startResendCountdown() {
      this.resendCountdown = 30;
      const timer = setInterval(() => {
        this.resendCountdown--;
        if (this.resendCountdown <= 0) clearInterval(timer);
      }, 1000);
    },
    resend() {
      if (this.resendBlocked || this.mfaMethod === MFA_METHODS.TOTP) return;
      this.isResending = true;

      window.sendMfaCode(this.mfaMethod, async (response) => {
        this.startResendCountdown();
        this.isResending = false;

        if (response != null) {
          this.errorMessage = "Failed to request Two-Factor Authentication code";
        }
      });
    },

    close() {
      this.$emit('close');
    },

    onInput() {
      this.clearError();

      // Sanitize: digits and uppercase only
      this.code = this.code.replace(/[^A-Z0-9]/gi, '').slice(0, this.codeLength).toUpperCase();
      this.$refs.codeInput.focus();
    },

    onPaste(e) {
      this.code = (e.clipboardData.getData('text') || '')
          .replace(/[^A-Z0-9]/gi, '')
          .toUpperCase()
          .slice(0, this.codeLength);

      this.$nextTick(() => this.$refs.codeInput.focus());
    },

    onEscape(e) {
      if (e.key === 'Escape' && this.isOpen) this.close();
    },

    async submit() {
      if (this.isLoading) return;

      if (!this.isComplete) {
        this.showError('Please enter your code before verifying.');
        return;
      }

      this.setLoading(true);

      fetch('/api/v1/mfa/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ method: this.mfaMethod, code: this.code }),
      })
        .then(res => res.json())
        .then(response => {
          if (response.sessionId != null) {
            this.$emit('verified');
          } else {
            this.showError('Please check your code and try again')
          }
        })
        .catch(_ => {
          this.showError('Something went wrong. Please try again.');
        });
    },

    showError(message) {
      this.errorMessage = message;
      this.isLoading = false;
      this.$refs.codeInput.focus();
    },

    clearError() {
      this.errorMessage = '';
    },

    reset() {
      this.code = '';
      this.errorMessage = '';
      this.isLoading = false;
      this.isResending = false;
    },

    setLoading(val) {
      this.isLoading = val;
    },
  },
};
</script>

<style scoped lang="scss">

.mfa-overlay {
  --mfa-text:              #323246;
  --mfa-text-body:         #161F28;
  --mfa-action:            #1A3149;
  --mfa-secondary:         #2271A5;
  --mfa-secondary-light:   #2271A559;
  --mfa-secondary-hover:   #1e3a5f0f;
  --mfa-error-color:       #dc2626;
  --mfa-transition:        160ms cubic-bezier(0.4, 0, 0.2, 1);
}

.mfa-overlay {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mfa-overlay .mfa-dialog {
  animation: mfa-slide-in var(--mfa-transition) both;
}

.mfa-overlay .mfa-backdrop {
  animation: mfa-fade-in 120ms ease both;
}

@keyframes mfa-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}

@keyframes mfa-slide-in {
  from { opacity: 0; transform: translateY(-8px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0)    scale(1);    }
}

.mfa-backdrop {
  position: fixed;
  inset: 0;
  background: #00000099;
  -ms-filter: "alpha(opacity=60)";
  backdrop-filter: blur(2px);
}

.mfa-dialog {
  position: relative;
  z-index: 1;
  width: min(480px, calc(100vw - 2rem));
  background: #FFF;
  border-radius: 4px;
  box-shadow: 0 8px 40px #0f172a2e, 0 2px 8px #0f172a14;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.mfa-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 12px 16px;
  border-bottom: 1px solid #EEEEF4;
}

.mfa-title {
  margin: 0;
  line-height: 24px;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0;
  color: var(--mfa-text);
}

.mfa-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--mfa-text);
  cursor: pointer;
  transition: background var(--mfa-transition), color var(--mfa-transition);
  flex-shrink: 0;
}

.mfa-close:hover { background: #f1f5f9; color: var(--mfa-text); }
.mfa-close:focus-visible { outline: 2px solid var(--mfa-action); outline-offset: 2px; }

.mfa-body {
  padding: 16px 24px 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mfa-description {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: var(--mfa-text-body);
}

.mfa-form { display: contents; }

.mfa-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mfa-label {
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  color: var(--mfa-text-body);
}

.mfa-field .mfa-form .mfa-input {
  border: 1px solid #DFE0E3;
  border-radius: 8px;
  color: #172530;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 24px;
  font-size: 16px;
  transition: border-color var(--mfa-transition), box-shadow var(--mfa-transition), background var(--mfa-transition);
  -moz-appearance: textfield;
  appearance: textfield;
}

.mfa-field .mfa-form .mfa-input:focus {
  outline: none;
  border-color: var(--mfa-action);
  box-shadow:  0 0 0 3px rgba(30, 58, 95, 0.12);
}

.mfa-field .mfa-form .mfa-input.is-filled { border-color: var(--mfa-action); }
.mfa-field .mfa-form .mfa-input.is-error  { border-color: var(--mfa-error-color); box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.12); }

.mfa-error {
  margin: 0;
  padding: 8px 12px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: var(--mfa-error-color);
}

.mfa-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px 24px;
  background: #F7F7FA;
}

.mfa-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background var(--mfa-transition), border-color var(--mfa-transition), color var(--mfa-transition);
  white-space: nowrap;
}

.mfa-btn:focus-visible { outline: 2px solid var(--mfa-action); outline-offset: 2px; }

.mfa-btn--resend {
  background: transparent;
  border: 1.5px solid var(--mfa-secondary);
  color: var(--mfa-secondary);
}
.mfa-btn--resend:hover { background: var(--mfa-secondary-hover); }
.mfa-btn--resend:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.mfa-btn--resend.is-loading { pointer-events: none; opacity: 0.75; }
.mfa-btn--resend.is-loading::after {
  content: '';
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-left: 8px;
  border: 2px solid var(--mfa-secondary-light);
  border-top-color: var(--mfa-secondary);
  border-radius: 50%;
  animation: mfa-loading-spin 0.7s linear infinite;
}

.mfa-btn--primary {
  background: var(--mfa-action);
  border: 1.5px solid transparent;
  color: #FFF;
}
.mfa-btn--primary:hover  { background: var(--mfa-secondary); }
.mfa-btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: var(--mfa-action);
}
.mfa-btn--primary.is-loading { pointer-events: none; opacity: 0.75; }
.mfa-btn--primary.is-loading::after {
  content: '';
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-left: 8px;
  border: 2px solid #FFFFFF59;
  border-top-color: #FFF;
  border-radius: 50%;
  animation: mfa-loading-spin 0.7s linear infinite;
}

@keyframes mfa-loading-spin { to { transform: rotate(360deg); } }
</style>